'use client'

import { useEffect, useRef } from 'react'
import { useSearchParams } from 'next/navigation'
import type { RoomAvailability } from '~/hooks/type'
import { useBookingCart } from '~/stores/booking-cart'
import { useHotelAvailabilitySearchStore } from '~/stores/hotel-availability-search'
import { useHotelAvailabilityStore } from '~/stores/hotel-availability'
import { useHotels } from '~/hooks/use-hotels'

export const AI_CHANNEL_REF = 'ai'

export function useIsAiChannel() {
  return useSearchParams().get('ref') === AI_CHANNEL_REF
}

const nightsBetween = (from: string, to: string) => {
  const nights: string[] = []
  const end = new Date(to).getTime()
  for (let t = new Date(from).getTime(); t < end; t += 24 * 60 * 60 * 1000) {
    nights.push(new Date(t).toISOString().slice(0, 10))
  }
  return nights
}

// Fills the cart from the `rooms` URL param for links coming from the AI
// channel (`?ref=ai`). The generic `rooms` handling in the desktop/mobile
// screens adds rooms as soon as availability lands, before the price lookup
// (mappedPrices) is ready, so every line ends up at 0đ. Here we wait until the
// price lookup belongs to the very search the URL describes, and apply once.
export function useAiChannelRooms() {
  const searchParams = useSearchParams()
  const { availability } = useHotelAvailabilityStore()
  const { addToCart, clearCart } = useBookingCart()
  const { hotels } = useHotels()
  const mappedPrices = useHotelAvailabilitySearchStore((state) => state.mappedPrices)
  const appliedRef = useRef<string | null>(null)

  const isAiChannel = searchParams.get('ref') === AI_CHANNEL_REF
  const roomsParam = searchParams.get('rooms')
  const checkInDate = searchParams.get('checkInDate')
  const checkOutDate = searchParams.get('checkOutDate')

  useEffect(() => {
    if (!isAiChannel || !roomsParam || appliedRef.current === roomsParam) return
    if (!availability || availability.length === 0) return
    if (!hotels || hotels.length === 0) return
    if (!checkInDate || !checkOutDate) return

    try {
      const rooms = JSON.parse(decodeURIComponent(roomsParam))
      if (!Array.isArray(rooms) || rooms.length === 0) return

      // mappedPrices is persisted in sessionStorage, so "non-empty" is not
      // enough: every requested room must have a price for each night of THIS
      // stay, otherwise it is stale (other dates) or not loaded yet.
      const nights = nightsBetween(checkInDate, checkOutDate)
      const pricesReady = rooms.every((roomData) => {
        const match = availability.find(
          (room) => room.roomCode === roomData.roomCode && room.available > 0
        )
        if (!match) return true // reported below, doesn't block the others
        const dates = new Set(mappedPrices[match.roomInfo?.roomCode || '']?.list.map((i) => i.date))
        return nights.length > 0 && nights.every((night) => dates.has(night))
      })
      if (!pricesReady) return

      appliedRef.current = roomsParam
      clearCart()

      rooms.forEach((roomData) => {
        const availableRoom = availability.find(
          (room) => room.roomCode === roomData.roomCode && room.available > 0
        )
        if (!availableRoom) {
          console.warn(`Room ${roomData.roomCode} not found in availability or not available`)
          return
        }
        const roomAvailability: RoomAvailability = {
          ...availableRoom,
          roomInfo: {
            ...availableRoom.roomInfo,
            baseAdult: roomData.adults,
            baseChild: roomData.kidsOver6 || 0,
          },
          guests: {
            adults: roomData.adults || 1,
            children6To12: roomData.kidsOver6 || 0,
            childrenUnder6: roomData.kidsUnder6 || 0,
          },
        }
        const customerCode = hotels.find(
          (h) => h.hotelId === roomAvailability.hotelId
        )?.customerCode
        addToCart(roomAvailability, 1, customerCode)
      })
    } catch (error) {
      console.error('Error parsing rooms from URL (AI channel):', error)
    }
  }, [
    isAiChannel,
    roomsParam,
    checkInDate,
    checkOutDate,
    availability,
    hotels,
    mappedPrices,
    addToCart,
    clearCart,
  ])
}
